/**
 * Game State Sync — Supabase Database Access Layer
 *
 * Pure functions for reading/writing game data to Supabase.
 * Every function handles its own errors silently (console.error only).
 * No errors propagate to callers — Supabase is purely additive.
 */

import { supabase } from './supabase';
import type { GameState } from '../types';

// --- Types ---

export type GameSessionStatus = 'in_progress' | 'won' | 'lost';

export type GameEventType =
  | 'game_started'
  | 'hint_revealed'
  | 'column_guess_correct'
  | 'column_guess_incorrect'
  | 'final_answer_submitted'
  | 'credits_exhausted'
  | 'game_won'
  | 'game_lost';

interface GameSessionRow {
  id: string;
  user_id: string;
  puzzle_id: string;
  status: GameSessionStatus;
  credits_remaining: number;
  credits_used: number;
  start_time: string;
  end_time: string | null;
}

interface BoardStateRow {
  id: string;
  session_id: string;
  solved_columns: {
    solved_columns: number[];
    column_answers: (string | null)[];
  };
  revealed_clues: boolean[][];
  guess_attempts: {
    guess_attempts: Record<number, number>;
    incorrect_guesses: Record<number, string[]>;
    final_answer_submitted: string | null;
  };
}

export interface ExistingSession {
  session: GameSessionRow;
  boardState: BoardStateRow | null;
}

// --- Helpers ---

function deriveStatus(gameState: GameState): GameSessionStatus {
  if (gameState.gameWon === true) return 'won';
  if (gameState.gameWon === false) return 'lost';
  return 'in_progress';
}

function toISOString(unixMs: number): string {
  return new Date(unixMs).toISOString();
}

// --- Read Operations ---

/**
 * Find an existing game session for this user + puzzle.
 * puzzleId is the UUID from puzzles_admin (or date string for legacy sessions).
 */
export async function findExistingSession(
  userId: string,
  puzzleId: string
): Promise<ExistingSession | null> {
  try {
    const { data: session, error: sessionError } = await supabase
      .from('game_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('puzzle_id', puzzleId)
      .single();

    if (sessionError || !session) {
      return null;
    }

    // Load board state for this session
    const { data: boardState, error: boardError } = await supabase
      .from('board_state')
      .select('*')
      .eq('session_id', session.id)
      .single();

    if (boardError) {
      console.error('[GameSync] Error loading board state:', boardError);
    }

    return {
      session: session as GameSessionRow,
      boardState: boardState as BoardStateRow | null,
    };
  } catch (err) {
    console.error('[GameSync] findExistingSession failed:', err);
    return null;
  }
}

// --- Write Operations ---

/**
 * Create a new game session + empty board state.
 * puzzleId is the UUID from puzzles_admin (or date string for legacy fallback).
 * Returns the session ID, or null on failure.
 */
export async function createSession(
  userId: string,
  puzzleId: string,
  startTime: number
): Promise<string | null> {
  try {
    // Create session
    const { data: session, error: sessionError } = await supabase
      .from('game_sessions')
      .insert({
        user_id: userId,
        puzzle_id: puzzleId,
        status: 'in_progress',
        credits_remaining: 10,
        credits_used: 0,
        start_time: toISOString(startTime),
      })
      .select('id')
      .single();

    if (sessionError || !session) {
      console.error('[GameSync] createSession failed:', sessionError);
      return null;
    }

    // Create initial board state
    const { error: boardError } = await supabase
      .from('board_state')
      .insert({
        session_id: session.id,
        solved_columns: {
          solved_columns: [],
          column_answers: [null, null, null, null],
        },
        revealed_clues: [
          [false, false, false, false, false, false],
          [false, false, false, false, false, false],
          [false, false, false, false, false, false],
          [false, false, false, false, false, false],
        ],
        guess_attempts: {
          guess_attempts: {},
          incorrect_guesses: {},
          final_answer_submitted: null,
        },
      });

    if (boardError) {
      console.error('[GameSync] createBoardState failed:', boardError);
    }

    return session.id;
  } catch (err) {
    console.error('[GameSync] createSession failed:', err);
    return null;
  }
}

/**
 * Update session + board state from current game state.
 */
export async function saveSessionState(
  sessionId: string,
  gameState: GameState
): Promise<void> {
  try {
    // Update game_sessions
    const sessionUpdate: Record<string, unknown> = {
      status: deriveStatus(gameState),
      credits_remaining: gameState.credits,
      end_time: gameState.endTime ? toISOString(gameState.endTime) : null,
      updated_at: new Date().toISOString(),
    };

    const { error: sessionError } = await supabase
      .from('game_sessions')
      .update(sessionUpdate)
      .eq('id', sessionId);

    if (sessionError) {
      console.error('[GameSync] saveSessionState (session) failed:', sessionError);
    }

    // Update board_state
    const boardUpdate = {
      solved_columns: {
        solved_columns: gameState.solvedColumns,
        column_answers: gameState.columnAnswers,
      },
      revealed_clues: gameState.revealedClues,
      guess_attempts: {
        guess_attempts: gameState.guessAttempts,
        incorrect_guesses: gameState.incorrectGuesses,
        final_answer_submitted: gameState.finalAnswerSubmitted,
      },
      updated_at: new Date().toISOString(),
    };

    const { error: boardError } = await supabase
      .from('board_state')
      .update(boardUpdate)
      .eq('session_id', sessionId);

    if (boardError) {
      console.error('[GameSync] saveSessionState (board) failed:', boardError);
    }
  } catch (err) {
    console.error('[GameSync] saveSessionState failed:', err);
  }
}

/**
 * Log a game event.
 */
export async function logGameEvent(
  sessionId: string,
  eventType: GameEventType,
  eventData: Record<string, unknown>
): Promise<void> {
  try {
    const { error } = await supabase
      .from('game_events')
      .insert({
        session_id: sessionId,
        event_type: eventType,
        event_data: eventData,
      });

    if (error) {
      console.error('[GameSync] logGameEvent failed:', error);
    }
  } catch (err) {
    console.error('[GameSync] logGameEvent failed:', err);
  }
}

/**
 * Update user profile stats when game ends.
 */
export async function updateUserStats(
  userId: string,
  won: boolean,
  score: number
): Promise<void> {
  try {
    // Fetch current stats
    const { data: profile, error: fetchError } = await supabase
      .from('user_profiles')
      .select('games_played, games_won, total_credits')
      .eq('id', userId)
      .single();

    if (fetchError || !profile) {
      console.error('[GameSync] Failed to fetch profile for stats update:', fetchError);
      return;
    }

    const updates: Record<string, number> = {
      games_played: profile.games_played + 1,
      total_credits: profile.total_credits + score,
    };

    if (won) {
      updates.games_won = profile.games_won + 1;
    }

    const { error: updateError } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId);

    if (updateError) {
      console.error('[GameSync] updateUserStats failed:', updateError);
    }
  } catch (err) {
    console.error('[GameSync] updateUserStats failed:', err);
  }
}

/**
 * Convert a Supabase board state back to GameState fields.
 * Used for cross-device restore.
 */
export function boardStateToGameState(
  session: GameSessionRow,
  boardState: BoardStateRow
): Partial<GameState> {
  return {
    credits: session.credits_remaining,
    startTime: new Date(session.start_time).getTime(),
    endTime: session.end_time ? new Date(session.end_time).getTime() : null,
    revealedClues: boardState.revealed_clues,
    solvedColumns: boardState.solved_columns.solved_columns,
    columnAnswers: boardState.solved_columns.column_answers,
    guessAttempts: boardState.guess_attempts.guess_attempts,
    incorrectGuesses: boardState.guess_attempts.incorrect_guesses,
    finalAnswerSubmitted: boardState.guess_attempts.final_answer_submitted,
    gameWon: session.status === 'won' ? true : session.status === 'lost' ? false : null,
    finalScore: null,
  };
}
